// Modern content block — clean, professional text section with optional decorative elements
"use client";

import React from "react";
import { motion } from "framer-motion";

interface ContentBlockProps {
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  lead?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /** Accent color for eyebrow (defaults to purple) */
  accentColor?: string;
}

export function ContentBlock({
  eyebrow,
  title,
  lead,
  children,
  className = "",
  accentColor = "#a855f7", // purple-500 default
}: ContentBlockProps) {
  return (
    <div className={`relative w-full ${className}`}>
      {/* Content container */}
      <div className="relative w-full">
        {/* Eyebrow */}
        {eyebrow && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="mb-3"
          >
            <span
              className="text-xs sm:text-sm font-semibold uppercase tracking-wider"
              style={{ color: accentColor }}
            >
              {eyebrow}
            </span>
          </motion.div>
        )}

        {/* Title */}
        {title && (
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 leading-snug"
          >
            {title}
          </motion.h2>
        )}

        {/* Lead paragraph */}
        {lead && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-sm sm:text-base font-light text-gray-600 leading-relaxed"
          >
            {lead}
          </motion.div>
        )}

        {/* Children content */}
        {children && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="prose prose-lg prose-gray max-w-none"
          >
            {children}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default ContentBlock;
